import { createSignal, createMemo, onCleanup, Show, For, type Component, type JSX } from "solid-js";
import { PanelLayout, type SubTab, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useWs } from "@/shared/ws";
import { Skeleton } from "@/components/skeleton";
import { Donut } from "./lib/donut";
import { PanelKpi, PanelKpiGrid, RefreshIndicator } from "@/shared/panel-primitives";
import { usePanelRefresh } from "@/shared/use-panel-refresh";
import { registerPanel } from "@/panel-registry";
import { isEnvelope } from "@/shared/ws-protocol";
import { ContextMenu, createContextMenu, type ContextMenuItem } from "@/components/context-menu";
import "./hyperv.css";

interface HyperVm {
  Id?: string;
  Name?: string;
  State?: string;
  CPUUsage?: number;
  MemoryAssigned?: number;
  Uptime?: string;
  Status?: string;
  Version?: string;
  Generation?: number;
  ProcessorCount?: number;
  NetworkBytesPerSecond?: number;
  NetworkReceivedBytesPerSecond?: number;
  NetworkSentBytesPerSecond?: number;
}

interface HyperNetworkAdapter {
  Name?: string;
  SwitchName?: string;
  MacAddress?: string;
  IPAddresses?: string[];
  VlanId?: number;
  Status?: string;
  BytesPerSecond?: number;
  BytesReceivedPerSecond?: number;
  BytesSentPerSecond?: number;
}

interface HyperSwitch {
  Name?: string;
  SwitchType?: string;
  NetAdapterInterfaceDescription?: string;
  Notes?: string;
}

interface ListResult<T> {
  rows?: T[];
  Rows?: T[];
}

type HyperVmDetail = Record<string, unknown>;

function listRows<T>(result: ListResult<T> | undefined): T[] {
  if (Array.isArray(result?.rows)) return result.rows;
  if (Array.isArray(result?.Rows)) return result.Rows;
  return [];
}

function formatMemory(bytes: unknown): string {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n <= 0) return "";
  if (n >= 1024 * 1024 * 1024) return (n / (1024 * 1024 * 1024)).toFixed(2) + " GB";
  if (n >= 1024 * 1024) return (n / (1024 * 1024)).toFixed(0) + " MB";
  return n + " B";
}

function formatRate(bytes: unknown): string {
  const text = formatMemory(bytes);
  return text ? `${text}/s` : "";
}

function formatCpu(value: unknown): string {
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  if (n === 0) return "0%";
  // Affiche une décimale sous 10 % pour faire apparaître les faibles charges.
  return `${n < 10 ? n.toFixed(1) : Math.round(n)}%`;
}

function propString(source: unknown, ...keys: string[]): string {
  const obj = source as Record<string, unknown> | null | undefined;
  for (const key of keys) {
    const value = obj?.[key];
    if (typeof value === "string" && value) return value;
  }
  return "";
}

function eventRows(payload: Record<string, unknown>, ...keys: string[]): HyperVm[] {
  for (const key of keys) {
    const value = payload[key];
    if (Array.isArray(value)) return value as HyperVm[];
  }
  return [];
}

function eventStrings(payload: Record<string, unknown>, ...keys: string[]): string[] {
  for (const key of keys) {
    const value = payload[key];
    if (Array.isArray(value)) return value.map((row) => String(row)).filter(Boolean);
  }
  return [];
}

function detailValue(source: unknown, ...keys: string[]): unknown {
  const obj = source as Record<string, unknown> | null | undefined;
  for (const key of keys) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return undefined;
}

function detailText(source: unknown, ...keys: string[]): string {
  const value = detailValue(source, ...keys);
  if (Array.isArray(value)) return value.join(", ");
  return value === undefined ? "-" : String(value);
}

function detailArray(source: unknown, ...keys: string[]): Record<string, unknown>[] {
  const value = detailValue(source, ...keys);
  return Array.isArray(value)
    ? (value.filter((row) => row && typeof row === "object") as Record<string, unknown>[])
    : [];
}

/** Icônes SVG inline pour les boutons de contrôle rapide. */
const ICON_START = (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M5 3.5v9l7-4.5-7-4.5z" fill="currentColor" />
  </svg>
);
const ICON_PAUSE = (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <path d="M5 3h2.2v10H5V3zm3.8 0H11v10H8.8V3z" fill="currentColor" />
  </svg>
);
const ICON_STOP = (
  <svg viewBox="0 0 16 16" aria-hidden="true">
    <rect x="4" y="4" width="8" height="8" rx="1" fill="currentColor" />
  </svg>
);

const HypervPanel: Component = () => {
  const ws = useWs();
  const confirm = useConfirm();
  const tools = useToolClient();
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);
  const [detailVmName, setDetailVmName] = createSignal("");
  const [contextVm, setContextVm] = createSignal<HyperVm | null>(null);
  const [live, setLive] = createSignal(false);
  const [liveRows, setLiveRows] = createSignal<HyperVm[]>([]);
  const [_liveWatchId, _setLiveWatchId] = createSignal("");
  const { menu: vmMenu, openAt: openVmMenu, close: closeVmMenu } = createContextMenu();
  let liveJobId = "";
  let offLive: (() => void) | null = null;
  let livePingTimer: ReturnType<typeof setInterval> | null = null;

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const vmsQuery = createToolQuery<ListResult<HyperVm>>(() => ({
    tool: "hyperv_vms_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const switchesQuery = createToolQuery<ListResult<HyperSwitch>>(() => ({
    tool: "hyperv_vswitches_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const vmDetailQuery = createToolQuery<HyperVmDetail>(() => ({
    tool: "hyperv_vm_get",
    params: detailVmName() ? { vmName: detailVmName() } : {},
    enabled: enabled() && Boolean(detailVmName()),
    refetchInterval: 0,
  }));

  const vmsRefresh = usePanelRefresh({
    query: () => ({
      isFetching: vmsQuery.isFetching,
      refetch: () => vmsQuery.refetch(),
      dataUpdatedAt: vmsQuery.dataUpdatedAt,
      error: vmsQuery.error,
    }),
  });
  const switchesRefresh = usePanelRefresh({
    query: () => ({
      isFetching: switchesQuery.isFetching,
      refetch: () => switchesQuery.refetch(),
      dataUpdatedAt: switchesQuery.dataUpdatedAt,
      error: switchesQuery.error,
    }),
  });

  const vmsLoading = () => vmsQuery.isFetching && !vmsQuery.data;
  const switchesLoading = () => switchesQuery.isFetching && !switchesQuery.data;
  const tableRows = () => (live() ? liveRows() : listRows(vmsQuery.data));
  const detailLiveRow = () => tableRows().find((vm) => vm.Name === detailVmName());

  function stopLivePing() {
    if (livePingTimer) clearInterval(livePingTimer);
    livePingTimer = null;
  }

  function startLivePing() {
    stopLivePing();
    livePingTimer = setInterval(() => {
      const jobId = liveJobId;
      if (!jobId || !live()) return;
      void tools.call("hyperv_watch_ping", { jobId }, { timeoutMs: 5_000 }).catch(() => {
        /* best-effort: le TTL agent nettoie les abonnements orphelins */
      });
    }, 8_000);
  }

  function applyLiveSnapshot(rows: HyperVm[]) {
    setLiveRows(rows);
  }

  function applyLiveDelta(add: HyperVm[], upd: HyperVm[], rm: string[]) {
    setLiveRows((current) => {
      const byId = new Map(current.map((row) => [row.Id || row.Name || "", row]));
      for (const id of rm) byId.delete(id);
      for (const row of add) byId.set(row.Id || row.Name || "", row);
      for (const row of upd)
        byId.set(row.Id || row.Name || "", { ...(byId.get(row.Id || row.Name || "") ?? {}), ...row });
      return Array.from(byId.values()).sort((a, b) => String(a.Name ?? "").localeCompare(String(b.Name ?? "")));
    });
  }

  async function stopLive() {
    const jobId = liveJobId;
    liveJobId = "";
    offLive?.();
    offLive = null;
    stopLivePing();
    setLive(false);
    _setLiveWatchId("");
    if (jobId) {
      try {
        await tools.call("hyperv_watch_stop", { jobId }, { timeoutMs: 10_000 });
      } catch {
        /* watcher TTL side agent */
      }
    }
  }

  async function startLive() {
    if (live() || !enabled()) return;
    try {
      const result = (await tools.call("hyperv_watch_start", { intervalMs: 5000 }, { timeoutMs: 20_000 })) as Record<
        string,
        unknown
      >;
      liveJobId = propString(result, "jobId", "JobId");
      const watchId = propString(result, "watchId", "WatchId");
      if (!liveJobId) throw new Error("jobId manquant");
      _setLiveWatchId(watchId);
      setLiveRows(listRows(vmsQuery.data));

      offLive?.();
      offLive = ws.on(
        (m) =>
          isEnvelope(m) &&
          m.family === "live" &&
          m.type === "hyperv" &&
          m.op.startsWith("watch.") &&
          (watchId ? propString(m.payload, "watchId", "WatchId") === watchId : true),
        (m) => {
          if (!isEnvelope(m)) return;
          const payload = (m.payload ?? {}) as Record<string, unknown>;
          if (m.op === "watch.snapshot") {
            applyLiveSnapshot(eventRows(payload, "vms", "Vms"));
          } else if (m.op === "watch.delta") {
            applyLiveDelta(
              eventRows(payload, "add", "Add"),
              eventRows(payload, "upd", "Upd"),
              eventStrings(payload, "rm", "Rm"),
            );
          } else if (m.op === "watch.error") {
            setActionStatus({ text: `Live Hyper-V: ${String(payload.e ?? payload.E ?? "erreur")}`, level: "danger" });
          } else if (m.op === "watch.stopped") {
            setLive(false);
            stopLivePing();
          }
        },
      );
      setLive(true);
      startLivePing();
      setActionStatus({
        text: `Live Hyper-V actif (${Number(result.intervalMs ?? result.IntervalMs ?? 5000) / 1000}s)`,
        level: "info",
      });
    } catch (err) {
      setActionStatus({
        text: `Live Hyper-V impossible: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  onCleanup(() => {
    void stopLive();
  });

  async function runVmAction(
    vmName: string,
    actionId: "start" | "stop" | "save" | "checkpoint" | "restore",
    label: string,
    level: "info" | "warn" | "danger",
  ): Promise<void> {
    if (!vmName) return;
    let checkpointName: string | undefined;
    if (actionId === "checkpoint") {
      const fallback = `cp-${new Date().toISOString().replace(/[:.]/g, "-")}`;
      const input = window.prompt("Nom du snapshot a creer :", fallback);
      if (input === null) return;
      checkpointName = input.trim() || fallback;
    }
    const ok = await confirm.confirm({
      title: `${label} - ${vmName}`,
      body: `Confirmer l'action Hyper-V "${label}" sur la VM ${vmName} ?`,
      level,
      confirmLabel: label,
      entityName: level === "danger" ? vmName : "",
    });
    if (!ok) return;
    setActionStatus({ text: `Execution ${label} sur ${vmName}...`, level: "info" });
    try {
      const params: Record<string, unknown> = { vmName, action: actionId };
      if (actionId === "stop") params.force = true;
      if (actionId === "restore") {
        const input = window.prompt("Nom du snapshot a restaurer :");
        if (input === null) return;
        const snapshotName = input.trim();
        if (!snapshotName) {
          setActionStatus({ text: "Nom de snapshot requis pour Restore.", level: "warn" });
          return;
        }
        params.snapshotName = snapshotName;
      }
      if (checkpointName) params.checkpointName = checkpointName;
      await tools.call("hyperv_vm_action", params, {
        userConfirmedAt: new Date().toISOString(),
      });
      setActionStatus({ text: `Action ${label} sur ${vmName} reussie.`, level: "info" });
      await vmsQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur action: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  function openVmDetails(row: HyperVm): void {
    const name = row.Name ?? "";
    if (!name) return;
    setDetailVmName(name);
    closeVmMenu();
  }

  function vmContextItems(row: HyperVm): ContextMenuItem[] {
    const stateLower = String(row.State ?? "").toLowerCase();
    const isRunning = stateLower === "running";
    const isOff = stateLower === "off";
    const name = row.Name ?? "";
    const destructiveDisabled = false;
    return [
      { label: "Details", onSelect: () => openVmDetails(row) },
      { separator: true },
      {
        label: "Start",
        disabled: !name || isRunning || destructiveDisabled,
        onSelect: () => void runVmAction(name, "start", "Start", "info"),
      },
      {
        label: "Save",
        disabled: !name || !isRunning || destructiveDisabled,
        onSelect: () => void runVmAction(name, "save", "Save", "warn"),
      },
      {
        label: "Stop",
        danger: true,
        disabled: !name || isOff || destructiveDisabled,
        onSelect: () => void runVmAction(name, "stop", "Stop", "danger"),
      },
      { separator: true },
      {
        label: "Checkpoint",
        disabled: !name || isOff || destructiveDisabled,
        onSelect: () => void runVmAction(name, "checkpoint", "Checkpoint", "info"),
      },
      {
        label: "Restore snapshot",
        disabled: !name || destructiveDisabled,
        onSelect: () => void runVmAction(name, "restore", "Restore", "warn"),
      },
    ];
  }

  function renderDetailField(label: string, value: unknown): JSX.Element {
    return (
      <div class="hv-detail-field">
        <span>{label}</span>
        <strong>{value === undefined || value === null || value === "" ? "-" : String(value)}</strong>
      </div>
    );
  }

  function liveOrDetail(row: HyperVm | undefined, detail: unknown, liveKey: keyof HyperVm, ...detailKeys: string[]) {
    const liveValue = row?.[liveKey];
    if (liveValue !== undefined && liveValue !== null && liveValue !== "") return liveValue;
    return detailValue(detail, ...detailKeys);
  }

  const detailModal = () => {
    const vmName = detailVmName();
    if (!vmName) return null;
    const detail = vmDetailQuery.data;
    const disks = detailArray(detail, "Disks", "disks");
    const adapters = detailArray(detail, "NetworkAdapters", "networkAdapters") as HyperNetworkAdapter[];
    const metrics = detailValue(detail, "Metrics", "metrics") as Record<string, unknown> | undefined;
    const row = detailLiveRow();
    const stateLower = String(row?.State ?? detailText(detail, "State", "state")).toLowerCase();
    const isRunning = stateLower === "running";
    const isOff = stateLower === "off";
    const actionsDisabled = false;
    return (
      <div
        class="hv-modal-overlay"
        role="dialog"
        aria-modal="true"
        aria-labelledby="hv-detail-title"
        onClick={(e) => {
          if (e.target === e.currentTarget) setDetailVmName("");
        }}
      >
        <div class="hv-modal">
          <div class="hv-modal__header">
            <div>
              <h3 id="hv-detail-title">{vmName}</h3>
              <p>{detailText(detail, "Host", "host")}</p>
            </div>
            <div class="hv-modal__actions">
              {iconBtn(
                ICON_START,
                "Demarrer",
                "start",
                isRunning || actionsDisabled,
                () => void runVmAction(vmName, "start", "Start", "info"),
              )}
              {iconBtn(
                ICON_PAUSE,
                "Mettre en pause (Save)",
                "warn",
                !isRunning || actionsDisabled,
                () => void runVmAction(vmName, "save", "Save", "warn"),
              )}
              {iconBtn(
                ICON_STOP,
                "Arreter",
                "stop",
                isOff || actionsDisabled,
                () => void runVmAction(vmName, "stop", "Stop", "danger"),
              )}
              <button
                type="button"
                class="btn btn--xs btn--info"
                disabled={isOff || actionsDisabled}
                onClick={() => void runVmAction(vmName, "checkpoint", "Checkpoint", "info")}
              >
                Checkpoint
              </button>
              <button type="button" class="btn btn--xs btn--ghost" onClick={() => setDetailVmName("")}>
                Fermer
              </button>
            </div>
          </div>
          <Show when={!vmDetailQuery.isFetching} fallback={<Skeleton variant="table" rows={5} />}>
            <div class="hv-modal__body">
              <section class="hv-detail-section">
                <h4>Configuration</h4>
                <div class="hv-detail-grid">
                  {renderDetailField("State", detailText(detail, "State", "state"))}
                  {renderDetailField("Status", detailText(detail, "Status", "status"))}
                  {renderDetailField("Version Hyper-V", detailText(detail, "Version", "version"))}
                  {renderDetailField("Generation", detailText(detail, "Generation", "generation"))}
                  {renderDetailField("Processeurs", detailText(detail, "ProcessorCount", "processorCount"))}
                  {renderDetailField("OS invité", detailText(detail, "GuestOSName", "guestOSName"))}
                  {renderDetailField("Version OS", detailText(detail, "GuestOSVersion", "guestOSVersion"))}
                  {renderDetailField("FQDN", detailText(detail, "GuestFqdn", "guestFqdn"))}
                  {renderDetailField("Adresses IP", detailText(detail, "IPAddresses", "ipAddresses"))}
                </div>
              </section>
              <section class="hv-detail-section">
                <h4>Temps réel</h4>
                <div class="hv-detail-grid">
                  {renderDetailField(
                    "CPU",
                    formatCpu(liveOrDetail(row, detail, "CPUUsage", "CPUUsage", "cpuUsage")) || "-",
                  )}
                  {renderDetailField(
                    "RAM",
                    formatMemory(liveOrDetail(row, detail, "MemoryAssigned", "MemoryAssigned", "memoryAssigned")) ||
                      "-",
                  )}
                  {renderDetailField("Uptime", detailText(detail, "Uptime", "uptime"))}
                  {renderDetailField(
                    "Réseau",
                    formatRate(
                      liveOrDetail(
                        row,
                        metrics,
                        "NetworkBytesPerSecond",
                        "NetworkBytesPerSecond",
                        "networkBytesPerSecond",
                      ),
                    ) || "-",
                  )}
                  {renderDetailField(
                    "RX",
                    formatRate(
                      liveOrDetail(
                        row,
                        metrics,
                        "NetworkReceivedBytesPerSecond",
                        "NetworkReceivedBytesPerSecond",
                        "networkReceivedBytesPerSecond",
                      ),
                    ) || "-",
                  )}
                  {renderDetailField(
                    "TX",
                    formatRate(
                      liveOrDetail(
                        row,
                        metrics,
                        "NetworkSentBytesPerSecond",
                        "NetworkSentBytesPerSecond",
                        "networkSentBytesPerSecond",
                      ),
                    ) || "-",
                  )}
                </div>
              </section>
              <section class="hv-detail-section">
                <h4>Disques</h4>
                <Show when={disks.length > 0} fallback={<div class="hv-detail-empty">Aucun disque remonté.</div>}>
                  <For each={disks}>
                    {(disk) => {
                      const size = detailValue(disk, "SizeBytes", "sizeBytes");
                      const max = detailValue(disk, "MaxSizeBytes", "maxSizeBytes");
                      const usage = detailValue(disk, "UsagePercent", "usagePercent");
                      const diskType = detailText(disk, "DiskType", "diskType");
                      const hasDiskType = diskType !== "-";
                      const usageText = usage === undefined ? "" : String(usage);
                      return (
                        <div class="hv-detail-row">
                          <span>
                            {detailText(disk, "ControllerType", "controllerType")}
                            <Show when={hasDiskType}> · {diskType}</Show>
                          </span>
                          <strong>{detailText(disk, "Path", "path")}</strong>
                          <em>
                            {size ? formatMemory(size as number) : "-"}
                            <Show when={max}> / {formatMemory(max as number)}</Show>
                            <Show when={usageText}> ({usageText}% utilisé)</Show>
                          </em>
                        </div>
                      );
                    }}
                  </For>
                </Show>
              </section>
              <section class="hv-detail-section">
                <h4>Réseau</h4>
                <Show
                  when={adapters.length > 0}
                  fallback={<div class="hv-detail-empty">Aucun adaptateur remonté.</div>}
                >
                  <For each={adapters}>
                    {(adapter) => (
                      <div class="hv-detail-row">
                        <span>{detailText(adapter, "Name", "name")}</span>
                        <strong>
                          {detailText(adapter, "SwitchName", "switchName")}
                          <Show when={adapter.VlanId}> · VLAN {adapter.VlanId}</Show>
                        </strong>
                        <em>
                          {detailText(adapter, "MacAddress", "macAddress")}
                          <Show when={adapter.IPAddresses?.length}> · {adapter.IPAddresses!.join(", ")}</Show>
                        </em>
                      </div>
                    )}
                  </For>
                </Show>
              </section>
            </div>
          </Show>
        </div>
      </div>
    );
  };

  // ─── KPI cards — calculés depuis les données déjà chargées ─────────────
  const kpis = createMemo(() => {
    const vms = tableRows();
    let running = 0;
    let alerts = 0;
    let ramAssigned = 0;
    for (const vm of vms) {
      if (String(vm.State ?? "").toLowerCase() === "running") running += 1;
      const statusLower = String(vm.Status ?? "").toLowerCase();
      if (statusLower && statusLower !== "operating normally" && statusLower !== "ok") {
        alerts += 1;
      }
      const ram = Number(vm.MemoryAssigned);
      if (Number.isFinite(ram) && ram > 0) ramAssigned += ram;
    }
    return {
      total: vms.length,
      running,
      stopped: vms.length - running,
      alerts,
      ramAssigned,
    };
  });

  const kpiCards = () => (
    <Show
      when={!vmsLoading()}
      fallback={
        <PanelKpiGrid>
          <For each={[0, 1, 2, 3]}>
            {() => (
              <div class="panel-kpi">
                <div class="panel-kpi__body">
                  <Skeleton variant="line" width="60%" height="11px" />
                  <Skeleton variant="line" width="40%" height="22px" />
                </div>
              </div>
            )}
          </For>
        </PanelKpiGrid>
      }
    >
      <PanelKpiGrid>
        <PanelKpi
          label="VMs allumees"
          value={`${kpis().running}/${kpis().total}`}
          intent="ok"
          leading={
            <Donut
              size={48}
              thickness={8}
              segments={[
                { label: "Allumees", value: kpis().running, color: "var(--accent-ok, #2faf5b)" },
                { label: "Eteintes", value: kpis().stopped, color: "var(--donut-track, rgba(127,127,127,0.3))" },
              ]}
            />
          }
        />
        <PanelKpi
          label="RAM allouee"
          value={formatMemory(kpis().ramAssigned) || "0 B"}
          sub="cumul VMs en marche"
          intent="info"
        />
        <PanelKpi
          label="Alertes"
          value={kpis().alerts}
          sub="statut non nominal"
          intent={kpis().alerts > 0 ? "warn" : "muted"}
        />
        <PanelKpi label="Total VMs" value={kpis().total} />
      </PanelKpiGrid>
    </Show>
  );

  /** Bouton-icône de contrôle rapide. */
  const iconBtn = (
    icon: JSX.Element,
    title: string,
    variant: "start" | "stop" | "warn",
    disabled: boolean,
    onClick: () => void,
  ): JSX.Element => (
    <button
      type="button"
      class={`hv-iconbtn hv-iconbtn--${variant}`}
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
    >
      {icon}
    </button>
  );

  const vmColumns: Column<HyperVm>[] = [
    { accessorKey: "Name", header: "Name" },
    { accessorKey: "State", header: "State" },
    {
      accessorKey: "CPUUsage",
      header: "CPU",
      cell: (info) => formatCpu(info.getValue()) || "-",
    },
    {
      accessorKey: "MemoryAssigned",
      header: "RAM",
      cell: (info) => formatMemory(info.getValue()) || "-",
    },
    {
      accessorKey: "NetworkBytesPerSecond",
      header: "Network",
      cell: (info) => formatRate(info.getValue()) || "-",
    },
    { accessorKey: "Uptime", header: "Uptime" },
    { accessorKey: "ProcessorCount", header: "Procs" },
    { accessorKey: "Status", header: "Status" },
  ];

  const switchColumns: Column<HyperSwitch>[] = [
    { accessorKey: "Name", header: "Name" },
    { accessorKey: "SwitchType", header: "SwitchType" },
    { accessorKey: "NetAdapterInterfaceDescription", header: "NetAdapter" },
    { accessorKey: "Notes", header: "Notes" },
  ];

  const subtabs: SubTab[] = [
    {
      id: "vms",
      label: "VMs",
      render: () => (
        <>
          {kpiCards()}
          <div class="panel-toolbar panel-toolbar--filter">
            <button type="button" class="btn btn--xs btn--info" onClick={() => vmsQuery.refetch()}>
              Rafraichir
            </button>
            <button
              type="button"
              class={`btn btn--xs ${live() ? "btn--warn" : "btn--info"}`}
              onClick={() => (live() ? void stopLive() : void startLive())}
            >
              {live() ? "Stop live" : "Live"}
            </button>
            <RefreshIndicator state={vmsRefresh} />
          </div>
          <Show when={!vmsLoading()} fallback={<Skeleton variant="table" rows={8} />}>
            <DataTable<HyperVm>
              rows={tableRows()}
              columns={vmColumns}
              onRowActivate={openVmDetails}
              onRowContextMenu={(row, event) => {
                setContextVm(row);
                openVmMenu(event);
              }}
              rowActions={(r) => {
                const stateLower = String(r.State ?? "").toLowerCase();
                const isRunning = stateLower === "running";
                const isOff = stateLower === "off";
                const name = r.Name ?? "";
                const destructiveDisabled = false;
                return (
                  <div class="hv-rowctl">
                    {/* Contrôles rapides — icônes toujours visibles */}
                    {iconBtn(ICON_START, "Demarrer", "start", isRunning || destructiveDisabled, () =>
                      runVmAction(name, "start", "Start", "info"),
                    )}
                    {iconBtn(ICON_PAUSE, "Mettre en pause (Save)", "warn", !isRunning || destructiveDisabled, () =>
                      runVmAction(name, "save", "Save", "warn"),
                    )}
                    {iconBtn(ICON_STOP, "Arreter", "stop", isOff || destructiveDisabled, () =>
                      runVmAction(name, "stop", "Stop", "danger"),
                    )}
                    <button type="button" class="btn btn--xs btn--info" onClick={() => openVmDetails(r)}>
                      Details
                    </button>
                    <button
                      type="button"
                      class="btn btn--xs btn--info"
                      disabled={isOff || destructiveDisabled}
                      onClick={() => runVmAction(name, "checkpoint", "Checkpoint", "info")}
                    >
                      Checkpoint
                    </button>
                    <button
                      type="button"
                      class="btn btn--xs btn--info"
                      disabled={destructiveDisabled}
                      onClick={() => runVmAction(name, "restore", "Restore", "warn")}
                    >
                      Restore
                    </button>
                  </div>
                );
              }}
            />
          </Show>
          <Show when={vmMenu.show() && contextVm()}>
            {(row) => <ContextMenu x={vmMenu.x()} y={vmMenu.y()} items={vmContextItems(row())} onClose={closeVmMenu} />}
          </Show>
        </>
      ),
    },
    {
      id: "vswitches",
      label: "vSwitches",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <button type="button" class="btn btn--xs btn--info" onClick={() => switchesQuery.refetch()}>
              Rafraichir
            </button>
            <RefreshIndicator state={switchesRefresh} />
          </div>
          <Show when={!switchesLoading()} fallback={<Skeleton variant="table" rows={6} />}>
            <DataTable<HyperSwitch> rows={listRows(switchesQuery.data)} columns={switchColumns} />
          </Show>
        </>
      ),
    },
  ];

  const status = (): PanelStatus | undefined => {
    if (actionStatus()) return actionStatus() ?? undefined;
    if (!ws.state.nativeAgentOnline) return { text: "Module natif requis.", level: "warn" };
    if (!ws.state.supportApproved) return { text: "Validation client requise.", level: "warn" };
    if (vmsQuery.isFetching) return { text: "Chargement...", level: "info" };
    const err = vmsQuery.error;
    if (err)
      return {
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      };
    return undefined;
  };

  return (
    <>
      <PanelLayout title="Hyper-V" subtabs={subtabs} status={status()} />
      {detailModal()}
    </>
  );
};

registerPanel("hyperv", HypervPanel);
export default HypervPanel;
